import { cn } from 'cn';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

export type QueueTab = { value: string; label: string; count?: number };

/**
 * The site's segmented pill: 40 px, the selected segment on paper with the
 * light shadow, counts in tabular figures. State lives in the URL so pinned
 * tabs deep-link; the screen owns the param and passes value and onChange.
 * Phone: a horizontal swipe strip with an edge fade.
 */
/**
 * The content a tab strip switches lives outside it (a table, a grid, a
 * panel), so the screen names that region with `panelId` and marks it
 * `role="tabpanel"`; the triggers then point at a real id. Without a
 * `panelId` no `aria-controls` is written at all, rather than the id of a
 * panel that does not exist (weekly review, 9 September 2026).
 */
export function QueueTabs({ tabs, value, onChange, label, panelId, className }: { tabs: QueueTab[]; value: string; onChange: (v: string) => void; label: string; panelId?: string; className?: string }) {
  return (
    <Tabs value={value} onValueChange={onChange} className={cn('max-w-full', className)}>
      <div className="relative max-w-full overflow-x-auto [mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)] md:[mask-image:none]">
        {/* 44 px segments on the phone, 32 px in the 40 px pill on desktop. */}
        <TabsList aria-label={label} className="h-[52px] gap-0.5 rounded-full border border-beam-line bg-beam-cream-deep p-1 md:h-10">
          {tabs.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              aria-controls={panelId}
              className="h-11 flex-none rounded-full px-3.5 text-sm text-beam-text-muted data-active:bg-beam-paper data-active:text-foreground data-active:shadow-beam-1 md:h-8"
            >
              {t.label}
              {t.count !== undefined ? <span className="tabular ml-1 text-beam-text-muted">{t.count}</span> : null}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
    </Tabs>
  );
}
